'use client';

import { Edit, Trash, Network, Transfer } from '@drift-labs/icons';
import React from 'react';
import { abbreviateAddress } from 'src/utils/strings';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import HealthDisplay from 'src/utils/HealthDisplay';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import Button from 'src/components/primitives/Button/Button';
import { cj } from 'src/utils/classNames';
import {
	AccountsTableItem,
	UseAccountsTable,
} from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/hooks/useAccountsTable';
import ReclaimableRentContent from 'src/components/ReclaimableRentContent';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';

interface Props extends Pick<
	UseAccountsTable,
	| 'isDelegateVariant'
	| 'openDepositModal'
	| 'openWithdrawModal'
	| 'openTransferModal'
	| 'openUpdateDelegateModal'
	| 'openEditAccountModal'
	| 'openDeleteAccountModal'
	| 'handleReclaimRent'
	| 'userStatsAccount'
	| 'claimingRentAccountId'
> {
	account: AccountsTableItem;
}

const AccountsTableMobileCard = ({
	account,
	isDelegateVariant,
	claimingRentAccountId,
	openDepositModal,
	openWithdrawModal,
	openTransferModal,
	openUpdateDelegateModal,
	openEditAccountModal,
	openDeleteAccountModal,
	handleReclaimRent,
	userStatsAccount,
}: Props) => {
	const {
		accountName,
		authority,
		acctNetValue,
		health,
		delegate,
		isDelegatedToNonEmbeddedWallet,
		isSuperStakeAccount,
		isLpAccount,
		isIsolatedAccount,
		acctBorrows,
		userId,
		acctSpotDeposits,
		hasClaimableRent,
		rent,
	} = account;

	const abbreviatedAuthority = abbreviateAddress(authority.toBase58());

	return (
		<MobileTableCard.Root className="gap-y-lg">
			<MobileTableCard.LabelWithValue
				labelClassName="typo-t2 text-text-default"
				label={
					<>
						{accountName}
						{isDelegateVariant && <span>({abbreviatedAuthority})</span>}
						{!isDelegateVariant &&
							!isSuperStakeAccount &&
							!isLpAccount &&
							!isIsolatedAccount && (
								<IconButton
									size="sm"
									variant="tertiary"
									className="-m-md p-md"
									onClick={() => openEditAccountModal(userId)}
									icon={Edit}
								/>
							)}
					</>
				}
				value={<HealthDisplay health={health} valueClassName="typo-t4" />}
			/>

			<div className="grid w-full grid-cols-3">
				<MobileTableCard.LabelWithValue
					label="Deposits"
					value={
						<NumberDisplay
							value={acctSpotDeposits}
							type="currency"
							isAccountValueToHide
						/>
					}
				/>

				<MobileTableCard.LabelWithValue
					label="Borrows"
					className="items-center"
					value={
						<NumberDisplay
							value={acctBorrows}
							type="currency"
							isAccountValueToHide
						/>
					}
				/>

				<MobileTableCard.LabelWithValue
					label="Net Value"
					className="items-end"
					value={
						<NumberDisplay
							value={acctNetValue}
							type="currency"
							isAccountValueToHide
						/>
					}
				/>
			</div>

			{!isDelegateVariant && (
				<>
					<div className="grid grid-cols-3">
						<MobileTableCard.LabelWithValue
							label="Health"
							value={<HealthDisplay health={health} valueClassName="typo-t4" />}
						/>

						<MobileTableCard.LabelWithValue
							label="Delegate"
							className="items-center"
							value={
								<span
									className={cj(
										'flex items-center gap-x-xs',
										!isDelegatedToNonEmbeddedWallet && 'text-text-tertiary'
									)}
								>
									{isDelegatedToNonEmbeddedWallet ? (
										<>
											{abbreviateAddress(delegate)}
											<IconButton
												size="sm"
												variant="tertiary"
												className="-m-sm p-sm"
												onClick={() =>
													openUpdateDelegateModal(userId, authority, delegate)
												}
												icon={Edit}
											/>
										</>
									) : (
										'-'
									)}
								</span>
							}
						/>

						<MobileTableCard.LabelWithValue
							label="Reclaimable Rent"
							className="items-end"
							value={
								rent.loaded && userStatsAccount ? (
									<ReclaimableRentContent
										userStatsAccount={userStatsAccount}
										accountDisplayData={account}
									/>
								) : (
									<SkeletonValuePlaceholder className="h-4 w-10" loading />
								)
							}
						/>
					</div>

					{/** Actions */}
					<div className="flex gap-x-sm">
						{hasClaimableRent && (
							<Button
								size="sm"
								variant="secondary"
								onClick={() => handleReclaimRent(userId)}
								isLoading={claimingRentAccountId === userId}
							>
								Claim
							</Button>
						)}

						<Button
							size="sm"
							variant="secondary"
							className="flex-1"
							onClick={() => openDepositModal(userId, authority)}
						>
							Deposit
						</Button>

						<Button
							size="sm"
							variant="secondary"
							className="flex-1"
							onClick={() => openWithdrawModal(userId, authority)}
						>
							Withdraw
						</Button>

						{!isIsolatedAccount && (
							<IconButton
								size="sm"
								variant="secondary"
								onClick={() => openTransferModal(userId, authority)}
								icon={Transfer}
							/>
						)}

						{!isDelegatedToNonEmbeddedWallet && (
							<IconButton
								size="sm"
								variant="secondary"
								onClick={() =>
									openUpdateDelegateModal(userId, authority, delegate)
								}
								icon={Network}
							/>
						)}

						<IconButton
							size="sm"
							variant="secondary"
							onClick={() => openDeleteAccountModal(userId)}
							icon={Trash}
						/>
					</div>
				</>
			)}
		</MobileTableCard.Root>
	);
};

export default React.memo(AccountsTableMobileCard);
